import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { sheets as engine } from '@aq/shared';
import type { ConflictPolicy } from './queries.ts';
import { api } from './api.ts';

export interface SpreadsheetSummary {
  id: string;
  name: string;
  folderId: string | null;
  isStarred: boolean;
  revision: number;
  sheetCount: number;
  createdAt: string;
  updatedAt: string;
}

export type SheetTab = engine.SheetData & {
  id: string;
  position: number;
  /**
   * `false` on a tab the server sent without its contents: a name, the size
   * of its grid, and empties. Opening a workbook sends every tab that way but
   * the first, so that one of three hundred tabs does not cost three hundred.
   * The editor fetches the rest as they are opened.
   *
   * Absent on a whole tab. The empties on a stub look exactly like a tab
   * somebody cleared, so everything that writes checks this first — a stub
   * saved is a tab wiped.
   */
  loaded?: false;
};

export interface SpreadsheetDocument extends SpreadsheetSummary {
  sheets: SheetTab[];
}

/**
 * A response from the server laid over what is already held.
 *
 * Adding, renaming and removing a tab all answer with the workbook's outline,
 * in which most tabs are stubs — including ones this browser has already
 * fetched, and may be editing. Taken as it came, the answer would put the
 * empties back over them. So a stub never replaces a whole tab: the whole one
 * stays, with the server's name and place for it. A whole tab in the answer
 * does replace, because that is the server saying what the tab now is.
 */
export function mergeDocument(
  held: SpreadsheetDocument | undefined,
  next: SpreadsheetDocument,
): SpreadsheetDocument {
  if (!held) return next;
  const whole = new Map(
    held.sheets.filter((tab) => tab.loaded !== false).map((tab) => [tab.id, tab]),
  );
  return {
    ...next,
    sheets: next.sheets.map((tab) => {
      if (tab.loaded !== false) return tab;
      const mine = whole.get(tab.id);
      return mine ? { ...mine, name: tab.name, position: tab.position } : tab;
    }),
  };
}

export interface SheetTemplate {
  id: string;
  name: string;
  description: string;
}

export const sheetKeys = {
  all: ['sheets'] as const,
  inFolder: (folderId: string | null) => ['sheets', 'folder', folderId] as const,
  one: (id: string) => ['sheets', id] as const,
  templates: ['sheets', 'templates'] as const,
};

export function useSpreadsheets(folderId: string | null) {
  return useQuery({
    queryKey: sheetKeys.inFolder(folderId),
    queryFn: () =>
      api.get<SpreadsheetSummary[]>(`/api/sheets${folderId ? `?folderId=${folderId}` : ''}`),
  });
}

export function useSpreadsheet(id: string) {
  return useQuery({
    queryKey: sheetKeys.one(id),
    queryFn: () => api.get<SpreadsheetDocument>(`/api/sheets/${id}`),
    enabled: Boolean(id),
    // The editor holds its own working copy and pushes deltas. Refetching
    // underneath it would discard keystrokes that have not been saved yet.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
}

export function useSheetTemplates() {
  return useQuery({
    queryKey: sheetKeys.templates,
    queryFn: () => api.get<SheetTemplate[]>('/api/sheets/templates'),
    staleTime: Infinity,
  });
}

export function useCreateSpreadsheet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      name: string;
      folderId?: string | null;
      template?: string;
      /** What to do about a name the folder already holds. */
      onConflict?: ConflictPolicy;
    }) => api.post<SpreadsheetDocument>('/api/sheets', input),
    onSuccess: () => void client.invalidateQueries({ queryKey: sheetKeys.all }),
  });
}

export function useRenameSpreadsheet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) =>
      api.post<SpreadsheetSummary>(`/api/sheets/${id}/rename`, { name }),
    onSuccess: () => void client.invalidateQueries({ queryKey: sheetKeys.all }),
  });
}

export function useMoveSpreadsheet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ id, folderId }: { id: string; folderId: string | null }) =>
      api.post<void>(`/api/sheets/${id}/move`, { folderId }),
    onSuccess: () => void client.invalidateQueries({ queryKey: sheetKeys.all }),
  });
}

export function useTrashSpreadsheet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete<void>(`/api/sheets/${id}`),
    onSuccess: () => void client.invalidateQueries({ queryKey: sheetKeys.all }),
  });
}

export function useAddTab(spreadsheetId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (name: string) =>
      api.post<SpreadsheetDocument>(`/api/sheets/${spreadsheetId}/tabs`, { name }),
    onSuccess: (document) =>
      client.setQueryData<SpreadsheetDocument>(sheetKeys.one(spreadsheetId), (held) =>
        mergeDocument(held, document),
      ),
  });
}

export function useRenameTab(spreadsheetId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sheetId, name }: { sheetId: string; name: string }) =>
      api.post<SpreadsheetDocument>(`/api/sheets/${spreadsheetId}/tabs/${sheetId}/rename`, {
        name,
      }),
    onSuccess: (document) =>
      client.setQueryData<SpreadsheetDocument>(sheetKeys.one(spreadsheetId), (held) =>
        mergeDocument(held, document),
      ),
  });
}

/**
 * Excel's Move or Copy, as two mutations over one question.
 *
 * `before` is the tab the moved or copied one lands in front of, and null is
 * Excel's "(move to end)" — a position would have to be kept in step with a
 * strip anybody else may be reordering, and "before that one" does not.
 */
export function useMoveTab(spreadsheetId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ sheetId, before }: { sheetId: string; before: string | null }) =>
      api.post<SpreadsheetDocument>(`/api/sheets/${spreadsheetId}/tabs/${sheetId}/move`, {
        before,
      }),
    onSuccess: (document) =>
      client.setQueryData<SpreadsheetDocument>(sheetKeys.one(spreadsheetId), (held) =>
        mergeDocument(held, document),
      ),
  });
}

export function useCopyTab(spreadsheetId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({
      sheetId,
      before,
      name,
    }: {
      sheetId: string;
      before: string | null;
      name?: string;
    }) =>
      api.post<SpreadsheetDocument>(`/api/sheets/${spreadsheetId}/tabs/${sheetId}/copy`, {
        before,
        ...(name === undefined ? {} : { name }),
      }),
    onSuccess: (document) =>
      client.setQueryData<SpreadsheetDocument>(sheetKeys.one(spreadsheetId), (held) =>
        mergeDocument(held, document),
      ),
  });
}

export function useRemoveTab(spreadsheetId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (sheetId: string) =>
      api.delete<SpreadsheetDocument>(`/api/sheets/${spreadsheetId}/tabs/${sheetId}`),
    onSuccess: (document) =>
      client.setQueryData<SpreadsheetDocument>(sheetKeys.one(spreadsheetId), (held) =>
        mergeDocument(held, document),
      ),
  });
}

/** Formats the importer accepts. Mirrors the server's list. */
export const IMPORTABLE_EXTENSIONS = ['.xlsx', '.xlsm', '.xls', '.csv', '.tsv', '.ods'];

export function isImportable(filename: string): boolean {
  const lower = filename.toLowerCase();
  return IMPORTABLE_EXTENSIONS.some((extension) => lower.endsWith(extension));
}

/** Open a workbook already in the Drive as an editable sheet. */
export function useOpenFileAsSheet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ fileId, name }: { fileId: string; name?: string }) =>
      api.post<SpreadsheetDocument>(`/api/sheets/from-file/${fileId}`, name ? { name } : {}),
    onSuccess: () => void client.invalidateQueries({ queryKey: sheetKeys.all }),
  });
}

/** Import a workbook straight from the user's machine. */
export function useImportSpreadsheet() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ file, folderId }: { file: File; folderId: string | null }) => {
      const form = new FormData();
      form.append('file', file);
      if (folderId) form.append('folderId', folderId);
      return api.upload<SpreadsheetDocument>('/api/sheets/import', form);
    },
    onSuccess: () => void client.invalidateQueries({ queryKey: sheetKeys.all }),
  });
}
